import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import Text from '@material-ui/core/Typography';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Divider from '@material-ui/core/Divider';
import User, {UserId} from 'records/User';
import NineBoxDataExportButtonContainer from '@components/nine-box/data-export/NineBoxDataExportButtonContainer';
import {Moment} from 'moment';
import {List, Map, Set} from 'immutable';
import {MetricInfo, RepChartPoint} from '@store/@types/metric';
import {TeamId} from 'records/TeamId';
import {CHART_DATA_FILTER} from 'components/dashboard/manager/nine-box/NineBox.const';

const MANAGER_PATTERN = '{{MANAGER}}' as const;

interface NineBoxHeaderProps {
  selectedDataPoint?: RepChartPoint;
  onSelectRep: (rep: UserId | null) => void;
  repFilter: CHART_DATA_FILTER;
  setRepFilter: (val: CHART_DATA_FILTER) => void;
  ownSummary: boolean;
  managerToDisplay: User;
  startDate: Moment;
  endDate: Moment;
  users: Map<UserId, User>;
  teams?: Set<TeamId>;
  exportOptions: List<MetricInfo>;
}

export const NineBoxHeader: React.FC<NineBoxHeaderProps> = ({
  ownSummary,
  repFilter,
  selectedDataPoint,
  onSelectRep,
  setRepFilter,
  managerToDisplay,
  startDate,
  endDate,
  teams,
  users,
  exportOptions,
}) => {
  const rep = selectedDataPoint?.rep;
  const getFilterTitle = (dataFilter: CHART_DATA_FILTER): string => {
    const name = managerToDisplay?.firstName ?? '...';
    switch (dataFilter) {
      case CHART_DATA_FILTER.ALL:
        return dataFilter;
      case CHART_DATA_FILTER.MANAGER_COACHING:
        return dataFilter.replace(MANAGER_PATTERN, ownSummary ? `I'm` : `${name} is`);
      case CHART_DATA_FILTER.MANAGER_NOT_COACHING:
        return dataFilter.replace(MANAGER_PATTERN, ownSummary ? `My` : `${name}'s`);
    }
  };
  const boardTitle = ownSummary ? 'YOUR' : `FOR ${managerToDisplay?.firstName ?? '...'}'s`;

  const {topRight, header, headerTitle, tabsDivider} = useStyles();
  return (
    <header className={header}>
      <div className={headerTitle}>
        {rep ? (
          <>
            <Button
              onClick={() => onSelectRep(null)}
              startIcon={<Icon className="material-icons-round">west</Icon>}>
              Back to Team
            </Button>
            <Text variant="h3">{rep.getDisplayName()} PROGRESS</Text>
            <div className={topRight}>
              <NineBoxDataExportButtonContainer
                startDate={startDate}
                endDate={endDate}
                teams={teams}
                users={users}
                selectedRep={rep}
                exportOptions={exportOptions}
              />
            </div>
          </>
        ) : (
          <>
            <Text variant="h3">COACHING {boardTitle} TEAM</Text>
            <Tabs
              indicatorColor="primary"
              value={repFilter}
              onChange={(_, val) => setRepFilter(val)}>
              {Object.entries(CHART_DATA_FILTER).map(([key, label]) => (
                <Tab key={key} value={label} label={getFilterTitle(label)} />
              ))}
            </Tabs>
            <div className={topRight}>
              <NineBoxDataExportButtonContainer
                startDate={startDate}
                endDate={endDate}
                teams={teams}
                users={users}
                exportOptions={exportOptions}
              />
            </div>
          </>
        )}
      </div>
      <Divider className={tabsDivider} />
    </header>
  );
};

const useStyles = makeStyles(theme => ({
  header: {
    height: theme.mixins.header.height,
    position: 'relative',
  },
  headerTitle: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    height: theme.mixins.header.height,
    padding: theme.spacing(0, 2),
    '& > button, h3': {
      marginRight: theme.spacing(2),
    },
  },
  tabsDivider: {
    marginTop: -2,
  },
  topRight: {
    position: 'absolute',
    top: 10,
    right: 5,
  },
}));
